
import Vue from "vue";
import { TranslateResult } from "vue-i18n";
import AppMultiSelectJournal from "@/components/MultiSelectJournal.vue";
import AppSelectBookYear from "@/components/bookyear/SelectBookYear.vue";
import AppMonthSelect from "@/components/common/MonthSelect.vue";
import AppSaleSearchBrowse from "@/components/sales/SaleSearchBrowse.vue";
import AppPurchaseSearchBrowse from "@/components/purchases/PurchaseSearchBrowse.vue";
import { Purchase, Sale } from "@/models/base";
import { ExactSyncService, IExactExportResult } from "@/services/exact-sync-service";
import { BookYearService } from "@/services/book-year-service";
import { Job } from "@/models/job";
import { JobService } from "@/services/job-service";
import { MainSocketService } from "@/services/main-socket-service";

export default Vue.extend({
	data() {
		return {
			state: "form" as "form" | "creating" | "done",
			targetTab: 0,
			bookYear: String(BookYearService.selectedBookYear),
			periodMode: "month" as "month" | "date" | "selection",
			startMonth: 1,
			endMonth: 12,
			beginDate: new Date(),
			endDate: new Date(),
			journalIds: [1] as number[],
			browseOpen: false,
			selectedSales: [] as Sale[],
			selectedPurchases: [] as Purchase[],
			includeAlreadySynced: false,
			job: null as Job | null,
			jobSubs: [] as string[],
			result: null as IExactExportResult | null
		};
	},
	props: {
		dossierName: {
			type: String,
			default: ""
		}
	},
	computed: {
		title():TranslateResult {
			if (this.dossierName) {
				return this.$ct("title-for", {name: this.dossierName});
			}
			return this.$ct("title");
		},
		targetTabs():string[] {
			return [this.$ct("target-sales").toString(), this.$ct("target-purchases").toString()];
		},
		periodModeItems():{value:string, text:string}[] {
			return [
				{value: "month", text: this.$ct("period-mode.month").toString()},
				{value: "date", text: this.$ct("period-mode.date").toString()},
				{value: "selection", text: this.$ct("period-mode.selection").toString()}
			];
		},
		currentSelection():(Sale | Purchase)[] {
			return this.targetTab == 1 ? this.selectedPurchases : this.selectedSales;
		}
	},
	methods: {
		close() {
			this.$emit("close");
		},
		onSalesPicked(sales:Sale[]) {
			this.selectedSales = sales;
			this.browseOpen = false;
		},
		onPurchasesPicked(purchases:Purchase[]) {
			this.selectedPurchases = purchases;
			this.browseOpen = false;
		},
		removeSelected(index:number) {
			if (this.targetTab == 1) {
				this.selectedPurchases.splice(index, 1);
			} else {
				this.selectedSales.splice(index, 1);
			}
		},
		async createFromSelection() {
			if (this.currentSelection.length == 0) {
				this.$wf.notify(this.$ct("no-selection").toString(), "error", 4000, "exclamation-triangle");
				return;
			}
			try {
				this.state = "creating";
				let j = this.targetTab == 1
					? await ExactSyncService.exportPurchasePeriod({
						purchases: this.selectedPurchases.map((p) => ({ID: p.ID, BookYear: p.BookYear})),
						includeAlreadySynced: this.includeAlreadySynced
					})
					: await ExactSyncService.exportPeriod({
						saleIds: this.selectedSales.map((s) => s.ID),
						includeAlreadySynced: this.includeAlreadySynced
					});
				this.subscribeToJob(j);
			} catch (err) {
				this.state = "form";
			}
		},
		async create() {
			if (this.periodMode == "selection") {
				await this.createFromSelection();
				return;
			}
			let by = Number(this.bookYear);
			let begin:Date;
			let end:Date;
			if (this.periodMode == "date") {
				begin = new Date(this.beginDate);
				end = new Date(this.endDate);
			} else {
				let yearStart = BookYearService.getBookYearStartDate(by);
				begin = new Date(yearStart);
				begin.setMonth(begin.getMonth() + (this.startMonth - 1));
				end = new Date(yearStart);
				end.setMonth(end.getMonth() + this.endMonth);
				end.setDate(end.getDate() - 1);
			}
			if (begin.getTime() > end.getTime()) {
				this.$wf.notify(this.$ct("invalid-period").toString(), "error", 4000, "exclamation-triangle");
				return;
			}
			try {
				this.state = "creating";
				let options = {
					bookYear: by,
					beginDate: begin,
					endDate: end,
					journalIds: this.journalIds,
					includeAlreadySynced: this.includeAlreadySynced
				};
				let j = this.targetTab == 1
					? await ExactSyncService.exportPurchasePeriod(options)
					: await ExactSyncService.exportPeriod(options);
				this.subscribeToJob(j);
			} catch (err) {
				this.state = "form";
			}
		},
		subscribeToJob(j:Job) {
			this.result = null;
			JobService.on(`job-started/${j.getID()}`, this.onJobStarted);
			JobService.on(`job-stopped/${j.getID()}`, this.onJobStopped);
			this.jobSubs.push(`job-started/${j.getID()}`);
			this.jobSubs.push(`job-stopped/${j.getID()}`);
			MainSocketService.on(`/plugins/exactonline/export-done/${j.getID()}`, this.onExportDone, this);
		},
		onJobStarted(job:Job) {
			this.job = job;
		},
		onJobStopped(job:Job) {
			if (this.result) return;
			if (job.Error) {
				this.state = "form";
				this.$wf.notify(this.$ct("export-error").toString(), "error", 5000, "exclamation-triangle");
			}
		},
		onExportDone(result:IExactExportResult) {
			this.result = result;
			this.state = "done";
			this.$emit("done");
		}
	},
	destroyed() {
		for (let sub of this.jobSubs) {
			JobService.off(sub, sub.startsWith("job-started") ? this.onJobStarted : this.onJobStopped);
		}
		MainSocketService.offAll(this);
	},
	components: {
		AppMultiSelectJournal,
		AppSelectBookYear,
		AppMonthSelect,
		AppSaleSearchBrowse,
		AppPurchaseSearchBrowse
	},
	i18nEx: {
		componentPrefix: "components.accountant-ExportToExactModal"
	}
});
